import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { B2BUnitHierarchyRepository } from '@asm/features/sync/repositories/b2bunit-hierarchy.repository';
import { ASM_UNIT_ID_KEY } from '@shared/tools/constants';

/**
 * Offline handler for the billing-clients endpoint.
 *
 * Replicates DefaultB2BUnitService.getBillingClients() using data from the
 * `all_b2bunits` store. Each record has:
 *   - `superUnits[]` / `childUnits[]` for hierarchy traversal
 *   - `rawAddresses[]` with `addressUsage` and `b2bUnitUID` for billing-client lookup
 *
 * Covers: GET /b2b-unit/billing-clients?b2bUnitId=&addressUsage=
 */
@Injectable({ providedIn: 'root' })
export class BillingClientsOfflineHandler {
  private readonly b2bUnitsRepo = inject(B2BUnitHierarchyRepository);

  canHandle(req: HttpRequest<unknown>): boolean {
    return req.method === 'GET' && req.url.includes('/b2b-unit/billing-clients');
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const b2bUnitId = req.params.get('b2bUnitId') ?? String(localStorage.getItem(ASM_UNIT_ID_KEY) ?? '').trim();
    const addressUsage = req.params.get('addressUsage') ?? '';

    if (!b2bUnitId || !addressUsage) {
      return new HttpResponse({ status: 400, body: [] });
    }

    const allUnits = await firstValueFrom(this.b2bUnitsRepo.getAll());

    // 1. Find the logged-in user's B2BUnit
    const unit = allUnits.find(u => u.uid === b2bUnitId);
    if (!unit) return new HttpResponse({ status: 200, body: [] });

    // 2. Collect superUnit UIDs
    const superUnitUids = (unit.superUnits ?? []).map(su => su.uid).filter(Boolean);
    if (!superUnitUids.length) return new HttpResponse({ status: 200, body: [] });

    // 3. For each superUnit, collect childUnit UIDs
    const childUnitUids = new Set<string>();
    for (const suUid of superUnitUids) {
      const superUnit = allUnits.find(u => u.uid === suUid);
      (superUnit?.childUnits ?? []).forEach(cu => childUnitUids.add(cu.uid));
    }

    // 4. Scan rawAddresses of child units for matching addressUsage and collect b2bUnitUIDs
    const billingUidSet = new Set<string>();
    for (const childUnit of allUnits.filter(u => childUnitUids.has(u.uid))) {
      for (const addr of childUnit.rawAddresses ?? []) {
        if (addr.addressUsage === addressUsage && addr.b2bUnitUID) {
          billingUidSet.add(addr.b2bUnitUID);
        }
      }
    }

    if (!billingUidSet.size) return new HttpResponse({ status: 200, body: [] });

    // 5. Return the B2BUnits whose UIDs match
    const result = allUnits
      .filter(u => billingUidSet.has(u.uid))
      .map(u => ({ uid: u.uid, name: u.name ?? u.uid, cnpj: u.cnpj }));

    return new HttpResponse({ status: 200, body: result });
  }
}
